import React from 'react'
import { Helmet } from 'react-helmet'
import ReactPlayer from 'react-player'
import { IonGrid, IonRow, IonHeader, IonToolbar, IonButtons, isPlatform, IonCol } from '@ionic/react'
import Slider from 'react-rangeslider'

import {
	ScreenSectionItem,
	SectionMediaVideoContainer as Container,
	SCREEN_SECTION_TYPE_WEB_VIDEO,
	formatTimeMS,
} from 'mytours-read-common'

import AudioTranscript from '../../../audio/components/AudioTranscript'
import { isRTL, isMobileOrTabletDevice } from '../../../platform'

import Loader from '../../../common/components/Loader'
import Button from '../../../common/components/Button'
import Modal from '../../../common/components/modal'
import classNames from 'classnames'

import strings from '../../../../strings'

import '../styles/fullScreenVideo.less'
import { ImageSizePreset } from '../../../../common_lib/types'
import { generateImageUrl, generateVideoUrl } from '../../../../common_lib/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { updateTranscriptionHash } from '../../functions'
import {
	getVideoUrl,
	isRemoteVideo,
	ReactPlayerConfig,
	sendVideoPauseEvent,
	sendVideoPlayEvent,
	sendVideoProgressEvent,
} from './functions'

interface OwnProps {
	sectionItem: ScreenSectionItem
	sectionId: number
	sectionTitle: string
	containerName: string
	onBack: () => void
	setFullScreenModal: (status: boolean) => void
	isFullScreenModal?: boolean
	isSingleItem?: boolean
	isOpen: boolean
}

interface State {
	isPaused: boolean
	showControls: boolean
	progress: number
	duration?: number
	userDraggingSlider: boolean
	showTranscriptModal: boolean
	isReady: boolean
}

type Props = OwnProps & Container.Props & Container.Actions

interface DocElementFullScreen {
	mozRequestFullScreen(): Promise<void>
	webkitRequestFullscreen(): Promise<void>
	msRequestFullscreen(): Promise<void>
}

interface DocElementExitFullScreen {
	mozCancelFullScreen(): Promise<void>
	webkitExitFullscreen(): Promise<void>
	msExitFullscreen(): Promise<void>
}

/**
 * The initial state for our internal component state.
 */
const INITIAL_STATE: State = {
	isPaused: true,
	showControls: true,
	userDraggingSlider: false,
	progress: 0,
	showTranscriptModal: false,
	isReady: false,
}

interface VideoProgressData {
	played: number
	playedSeconds: number
	loaded: number
	loadedSeconds: number
}

interface WebKitDocument {
	fullscreenElement: any
	mozFullScreenElement: any
	webkitFullscreenElement: any
}

class FullScreenVideo extends React.Component<Props, State> {
	state = INITIAL_STATE

	player: ReactPlayer | null
	isVideoStarted = false

	private hash = ''
	private unListen: () => void
	private progressBreakpoints = {
		10: false,
		25: false,
		50: false,
		75: false,
	}

	componentDidMount() {
		const {
			sectionItem: { file },
			videoFilePath,
		} = this.props

		if (!videoFilePath && file) {
			const url = generateVideoUrl(file, {
				pathType: file.dont_transform ? 'original' : 'video_generic480p',
			})
			this.props.loadVideoPath(file.filename, url)
		}

		addEventListener('fullscreenchange', this.fullscreenChange)

		this.unListen = ionHistory.listen(({ hash }) => {
			if (hash !== this.hash) {
				// TODO: this is temp fix: add global handle for hash changes
				if (hash === '' && this.hash === '#transcript' && this.state.showTranscriptModal) {
					this.toggleTranscriptModal(false)
				}

				this.hash = hash
			}
		})
	}

	componentWillUnmount(): void {
		removeEventListener('fullscreenchange', this.fullscreenChange)

		this.unListen()
	}

	componentDidUpdate(): void {
		const document: any = window.document
		const doc: Document & WebKitDocument = document

		const fullscreenElement = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement

		if (Boolean(fullscreenElement) !== this.props.isFullScreenModal) {
			this.props.setFullScreenModal(Boolean(fullscreenElement))
		}
	}

	fullscreenChange = (): void => {
		const document: any = window.document
		const doc: Document & WebKitDocument = document

		const fullscreenElement = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement
		// Close video modal if it is only media item in section and native player is used
		if (!fullscreenElement && this.props.isSingleItem && isMobileOrTabletDevice()) {
			this.props.onBack()
		}
		this.forceUpdate()
	}

	get isLoadingVideoPath(): boolean {
		const {
			videoFilePath,
			loadingVideoPath,
			sectionItem: { media_type: mediaType },
		} = this.props
		if (mediaType === SCREEN_SECTION_TYPE_WEB_VIDEO) {
			return false
		}

		return loadingVideoPath || videoFilePath === undefined || videoFilePath === null
	}

	get playPauseButtonIcon(): string {
		return this.state.isPaused ? 'play' : 'pause'
	}

	onPlayPausePress = () => {
		this.setState((prev: State) => ({ isPaused: !prev.isPaused }))
	}

	onSliderValueChanged = (value: number) => {
		// update user dragging state (but only if we need to as this callback gets hit a lot while dragging the slider)
		if (!this.state.userDraggingSlider) {
			this.setState({ userDraggingSlider: true })
		}

		if (value === 100) {
			this.player.seekTo(0)
			this.setState({ isPaused: true })
		}

		this.setState({ progress: value })
	}

	onSlidingComplete = () => {
		const { progress } = this.state
		const value = Math.round(progress)
		const { duration } = this.state

		if (this.player && duration) {
			const newTime = duration * (value / 100)

			if (newTime === duration) {
				this.player.seekTo(0)
				this.setState({ isPaused: true })
			} else {
				this.player.seekTo(newTime)
			}
		}
		// note we set the new current time on state immediately for cases like when the user was dragging slider but audio completed before user let go of slider.
		// in that scenario the slider and time text updates to correct position but audio does not play until user presses Play button again
		this.setState({ userDraggingSlider: false })
	}

	onProgress = (data: VideoProgressData) => {
		const { userDraggingSlider, progress } = this.state
		const videoDuration = this.player.getDuration() || 1
		const newProgress = userDraggingSlider ? progress : (data.playedSeconds / videoDuration) * 100

		sendVideoProgressEvent(this.player, this.videoEventParams, data.played, this.progressBreakpoints)

		if (newProgress === 100) {
			this.player.seekTo(0)
			this.setState({ isPaused: true })
		}
		this.setState({ duration: videoDuration, progress: newProgress })
	}

	onTranscriptPressed = () => {
		const { sectionItem } = this.props
		if (sectionItem.transcription) {
			this.toggleTranscriptModal(true)
		}
	}

	toggleTranscriptModal = (value?: boolean): void => {
		this.setState((prev: State) => {
			updateTranscriptionHash(value, prev.showTranscriptModal, () => {
				// TODO: add `video_view_transcript` event
			})

			return {
				showTranscriptModal: typeof value === 'boolean' ? value : !prev.showTranscriptModal,
			}
		})
	}

	renderTranscriptModal = (): JSX.Element => {
		const { sectionItem } = this.props
		const { showTranscriptModal } = this.state

		return (
			<Modal isOpen={showTranscriptModal} onDidDismiss={() => this.toggleTranscriptModal(false)}>
				<AudioTranscript transcript={sectionItem.transcription} onBack={this.toggleTranscriptModal} />
			</Modal>
		)
	}

	private toggleControls = () => {
		this.setState((prev: State) => ({ showControls: !prev.showControls }))
	}

	handleControls = e => {
		const {
			target: { attributes, parentElement },
		} = e
		const hasAttribute: boolean = attributes?.['player-action-attribute']
		const parentHasAttribute: boolean = parentElement?.attributes?.['player-action-attribute']

		if (hasAttribute || parentHasAttribute) {
			this.setState({ showControls: true })
		} else {
			this.toggleControls()
		}
	}

	private requestFullScreen = () => {
		const { setFullScreenModal } = this.props
		const element = window.document.documentElement as HTMLElement & DocElementFullScreen

		if (element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen) {
			if (setFullScreenModal) setFullScreenModal(true)
		}

		if (element.requestFullscreen) {
			element.requestFullscreen()
		} else if (element.webkitRequestFullscreen) {
			element.webkitRequestFullscreen()
		} else if (element.mozRequestFullScreen) {
			element.mozRequestFullScreen()
		}
	}

	private fullScreenCancel = () => {
		const { setFullScreenModal } = this.props
		const doc = window.document as Document & DocElementExitFullScreen

		if (doc.exitFullscreen || doc.mozCancelFullScreen || doc.msExitFullscreen || doc.webkitExitFullscreen) {
			if (setFullScreenModal) setFullScreenModal(false)
		}

		if (doc.exitFullscreen) {
			doc.exitFullscreen()
		} else if (doc.mozCancelFullScreen) {
			/* Firefox */
			doc.mozCancelFullScreen()
		} else if (doc.webkitExitFullscreen) {
			/* Chrome, Safari and Opera */
			doc.webkitExitFullscreen()
		} else if (doc.msExitFullscreen) {
			/* IE/Edge */
			doc.msExitFullscreen()
		}
	}

	onFullScreenToggle = (): void => {
		const { isFullScreenModal } = this.props
		if (isFullScreenModal) {
			this.fullScreenCancel()
		} else {
			this.requestFullScreen()
		}
	}

	onClose = (): void => {
		const { setFullScreenModal, onBack } = this.props
		if (setFullScreenModal) setFullScreenModal(false)
		if (onBack) onBack()
	}

	onReady = (player: ReactPlayer): void => {
		if (this.state.isReady) {
			return
		}

		this.player = player
		this.setState({ isReady: true, isPaused: !this.props.isOpen })

		setTimeout(() => {
			if (!this.state.isPaused) {
				this.setState({ showControls: false })
			}
		}, 2000)
	}

	onError = (): void => {
		// ipad ios doesn't support autoplay, we pause video and then user should tap on play button
		// react-player version 2.7.0
		this.setState({ isReady: true, isPaused: true })
	}

	get videoEventParams() {
		const { sectionItem, sectionId, sectionTitle, containerName } = this.props

		return {
			sectionItemName: sectionItem.name,
			sectionId,
			sectionTitle,
			containerName,
		}
	}

	onPlay = () => {
		sendVideoPlayEvent(this.player, this.videoEventParams, this.isVideoStarted)
		this.isVideoStarted = true
	}

	onPause = () => {
		sendVideoPauseEvent(this.player, this.videoEventParams)
	}

	onVideoEnd = () => {
		this.setState({ isPaused: true }, () => {
			this.player.seekTo(0)
		})

		this.isVideoStarted = false
		this.progressBreakpoints = {
			10: false,
			25: false,
			50: false,
			75: false,
		}
	}

	render() {
		const {
			sectionItem: { thumbnail_media_item: thumbnailMediaItem, transcription, title, remote_type: remoteType },
			isFullScreenModal,
			videoFilePath,
		} = this.props
		const { isPaused, showControls, progress, isReady, duration } = this.state

		const isLoading = this.isLoadingVideoPath || !isReady
		const hasTranscript = !!transcription

		const imageUrl: string =
			thumbnailMediaItem?.file &&
			generateImageUrl(thumbnailMediaItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			})

		const filename = thumbnailMediaItem?.file?.filename || title || ''

		const schema = {
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@type': 'VideoObject',
				name: filename,
				description: filename,
				thumbnailUrl: imageUrl,
			}),
		}
		const url = getVideoUrl(videoFilePath, this.props.sectionItem)
		const isEmbed = isRemoteVideo(remoteType)
		const showVideoControls = showControls && isReady && !isEmbed

		const currentTime = this.player?.getCurrentTime()

		const elapsedTime = currentTime ? Math.round(currentTime) : 0
		const formattedDuration = duration ? Math.round(duration) : 0
		const formattedElapsedTime = formatTimeMS(elapsedTime)
		const formattedDurationTime = formatTimeMS(formattedDuration)

		return (
			<>
				<Helmet script={[schema]} />
				<IonGrid
					className={classNames('fullscreen-video-container', {
						embed: isEmbed,
					})}
					data-cy="fullscreen-video-container"
				>
					<IonRow className="fullscreen-video-row" onClick={this.handleControls}>
						<IonCol>
							<IonRow className="header-row">
								<IonHeader className="header fullscreen-video-header">
									<IonToolbar className="header-toolbar">
										{(showControls || isEmbed) && (
											<IonButtons slot="end" className="header-buttons">
												<Button
													className="back-button"
													onClick={this.onClose}
													player-action-attribute="player-action-attribute"
													ariaLabel={strings.accessibility_go_back}
												>
													<i className="fal fa-xmark close-icon" />
												</Button>
											</IonButtons>
										)}
									</IonToolbar>
								</IonHeader>
							</IonRow>

							{isLoading && (
								<IonRow className="loading-row">
									<IonRow>
										<Loader />
									</IonRow>
								</IonRow>
							)}

							<IonRow className="player-row">
								<IonRow className="player-row-inner">
									<ReactPlayer
										url={url}
										playing={!isPaused}
										onProgress={this.onProgress}
										width="100%"
										height="100%"
										onReady={this.onReady}
										onError={this.onError}
										onPlay={this.onPlay}
										onPause={this.onPause}
										onEnded={this.onVideoEnd}
										muted={false}
										config={ReactPlayerConfig}
									/>
									{showVideoControls && (
										<div className="controls-wrapper">
											<div className="controls">
												<Button
													className="control-button"
													fill="clear"
													onClick={this.onPlayPausePress}
													ariaLabel={isPaused ? strings.play_media : strings.pause_media}
												>
													<i
														className={`fas fa-${this.playPauseButtonIcon}`}
														player-action-attribute="player-action-attribute"
													/>
												</Button>
											</div>
										</div>
									)}
								</IonRow>
							</IonRow>
							{showVideoControls && (
								<IonRow
									className="video-range-row"
									style={{ paddingBottom: isPlatform('ios') ? 10 : 20 }}
									player-action-attribute="player-action-attribute"
								>
									<IonCol>
										<IonCol className="duration-col">{formattedElapsedTime} </IonCol>
										<Slider
											className={`slider-range ${isPlatform('ios') && 'slider-range-ios'}`}
											max={100}
											min={0}
											value={progress}
											tooltip={false}
											onChange={this.onSliderValueChanged}
											onChangeComplete={this.onSlidingComplete}
											reverse={isRTL()}
											player-action-attribute="player-action-attribute"
										/>
										<IonCol className="duration-col">{formattedDurationTime}</IonCol>
									</IonCol>
									{hasTranscript && (
										<IonRow className="video-transcript-row">
											<Button
												fill="clear"
												className="transcript-button"
												ariaLabel={strings.transcript}
												onClick={this.onTranscriptPressed}
											>
												<i className="fal fa-align-left" player-action-attribute="player-action-attribute" />
											</Button>
										</IonRow>
									)}
									<IonRow className="expand-row">
										<Button
											fill="clear"
											className="transcript-button"
											onClick={this.onFullScreenToggle}
											ariaLabel={
												isFullScreenModal
													? strings.accessibility_close_fullscreen_player
													: strings.accessibility_open_fullscreen_player
											}
										>
											<i className={`fas fa-${isFullScreenModal ? 'compress-alt' : 'expand-alt'}`} />
										</Button>
									</IonRow>
								</IonRow>
							)}
						</IonCol>
					</IonRow>
					{this.renderTranscriptModal()}
				</IonGrid>
			</>
		)
	}
}

const FullscreenVideoPlayer = Container.default(FullScreenVideo)
export default FullscreenVideoPlayer as any
